// Nuevo componente Users.js con exportación
import { useEffect, useState, useContext } from 'react';
import api from '../api/api';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar, Alert, Select, MenuItem, InputLabel, FormControl,
} from '@mui/material';
import { DataGrid, GridToolbarExport, GridToolbarContainer } from '@mui/x-data-grid';
import { AuthContext } from '../context/AuthContext';

const Users = () => {
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ id: null, name: '', email: '', role: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectionModel, setSelectionModel] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (error) {
      console.error('Error fetching users', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch =
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter ? u.role === roleFilter : true;
    return matchesSearch && matchesRole;
  });

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setFormData({ id: user.id, name: user.name, email: user.email, role: user.role });
    } else {
      setFormData({ id: null, name: '', email: '', role: '' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.role) {
      setSnackbar({ open: true, message: 'Complete todos los campos.', severity: 'warning' });
      return;
    }
    const token = localStorage.getItem('token');
    try {
      if (formData.id) {
        const res = await api.put(`/users/${formData.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(prev => prev.map(u => u.id === formData.id ? res.data : u));
        setSnackbar({ open: true, message: 'Usuario actualizado.', severity: 'success' });
      } else {
        const res = await api.post('/users', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(prev => [...prev, res.data]);
        setSnackbar({ open: true, message: 'Usuario creado.', severity: 'success' });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error al guardar usuario.', severity: 'error' });
    } finally {
      setDialogOpen(false);
    }
  };

  const handleDeleteSelected = async () => {
    const token = localStorage.getItem('token');
    try {
      for (const id of selectionModel) {
        await api.delete(`/users/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setUsers(prev => prev.filter(u => !selectionModel.includes(u.id)));
      setSnackbar({ open: true, message: 'Usuarios eliminados.', severity: 'success' });
      setSelectionModel([]);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error al eliminar.', severity: 'error' });
    }
  };

  const columns = [
    { field: 'name', headerName: 'Nombre', flex: 1, sortable: true },
    { field: 'email', headerName: 'Email', flex: 1, sortable: true },
    { field: 'role', headerName: 'Rol', flex: 0.5, sortable: true },
    {
      field: 'actions', headerName: 'Acciones', flex: 0.7, sortable: false,
      renderCell: (params) => (
        <Button size="small" variant="outlined" onClick={() => handleOpenDialog(params.row)}>Editar</Button>
      )
    }
  ];

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarExport csvOptions={{ fileName: 'usuarios_exportados' }} />
    </GridToolbarContainer>
  );

  return (
    <Box sx={{ height: '100%', width: '100%', p: 2 }}>
      <Typography variant="h5" gutterBottom>Gestión de Usuarios</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Buscar"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Rol</InputLabel>
          <Select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            label="Rol"
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="admin">Administrador</MenuItem>
            <MenuItem value="user">Usuario</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          sx={{ backgroundColor: '#E7A432', color: '#163244', fontSize: '0.85rem', height: 40 }}
          onClick={() => handleOpenDialog()}
        >
          Agregar
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={handleDeleteSelected}
          disabled={selectionModel.length === 0}
        >
          Eliminar
        </Button>
      </Box>
      <DataGrid
        rows={filteredUsers}
        columns={columns}
        autoHeight
        checkboxSelection
        disableRowSelectionOnClick
        loading={loading}
        onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
        selectionModel={selectionModel}
        getRowId={(row) => row.id}
        components={{ Toolbar: CustomToolbar }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{formData.id ? 'Editar Usuario' : 'Agregar Usuario'}</DialogTitle>
        <DialogContent>
          <TextField label="Nombre" name="name" fullWidth margin="dense" value={formData.name} onChange={handleInputChange} />
          <TextField label="Email" name="email" fullWidth margin="dense" value={formData.email} onChange={handleInputChange} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Rol</InputLabel>
            <Select name="role" value={formData.role} label="Rol" onChange={handleInputChange}>
              <MenuItem value="admin">Administrador</MenuItem>
              <MenuItem value="user">Usuario</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ color: '#163244', borderColor: '#E7A432' }}
            variant="outlined"
          >Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ backgroundColor: '#E7A432', color: '#163244' }}
          >Guardar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
